import { nowInstant } from "@nijiurachan/js/util/time";
import {
  apiGet,
  apiPost,
  apiPutJson,
  uploadAttachment,
} from "@/shared/api/client";

export interface EventOccurrence {
  startsAt: string;
  url?: string;
  state: "SCHEDULED" | "PENDING" | "CREATED" | "FAILED";
  count: number;
  judgedAt: string | null;
  threadId: string | null;
  reason: string | null;
}
export type CalendarRecurrenceEnd =
  | { kind: "never" }
  | { kind: "count"; count: number }
  | { kind: "until"; date: string };
export type CalendarRecurrence =
  | { kind: "none" }
  | {
      kind: "weekly";
      weekdays: number[];
      end: CalendarRecurrenceEnd;
    }
  | { kind: "monthly"; end: CalendarRecurrenceEnd };
export interface CalendarEvent {
  firstStartsAt: string;
  lastStartsAt: string | null;
  attachments?: {
    id: string;
    mime: string;
    url: string;
    thumbnailUrl: string;
  }[];
  id: string;
  slug: string;
  recurrence: CalendarRecurrence;
  content: {
    title: string;
    body: string;
    threshold: number;
    attachmentIds: string[];
    durationMinutes?: number;
    poll?: { options: string[]; durationMinutes: number };
    moderation?: { image?: { nsfw?: string } } | null;
  };
  occurrences: EventOccurrence[];
}
export interface EventBoard {
  slug: string;
  boardName: string;
  allowR18ThreadCreation: boolean;
}

export function maskCalendarEvent(
  event: CalendarEvent,
  showR18: boolean,
): CalendarEvent {
  if (showR18 || event.content.moderation?.image?.nsfw !== "off") return event;
  return {
    ...event,
    attachments: [],
    content: {
      ...event.content,
      body: "R18の内容は表示設定により非表示です",
      attachmentIds: [],
      poll: undefined,
    },
  };
}
export interface EventResult {
  judgedAt: string;
}
export interface EventResults {
  interested: EventResult[];
  organized: EventResult[];
}

export type CalendarRepeatKind = CalendarRecurrence["kind"];
export type CalendarRecurrenceEndKind = CalendarRecurrenceEnd["kind"];
export interface CalendarRegistrationErrors {
  duration?: string;
  title?: string;
  startsAt?: string;
  image?: string;
  threshold?: string;
  count?: string;
  until?: string;
  pollOptions?: string;
  pollDuration?: string;
}
export type CalendarOccurrenceStatus =
  | "scheduled"
  | "pending"
  | "created"
  | "failed"
  | "cancelled";

export function getCalendarFormVisibility(
  repeat: CalendarRepeatKind,
  end: CalendarRecurrenceEndKind,
): {
  showWeekdays: boolean;
  showEnd: boolean;
  showCount: boolean;
  showUntil: boolean;
} {
  const showEnd = repeat !== "none";
  return {
    showWeekdays: repeat === "weekly",
    showEnd,
    showCount: showEnd && end === "count",
    showUntil: showEnd && end === "until",
  };
}

export function calendarRecurrenceFromForm(form: FormData): CalendarRecurrence {
  const repeat = form.get("repeat");
  if (repeat !== "weekly" && repeat !== "monthly") return { kind: "none" };

  const endKind = form.get("end");
  const end: CalendarRecurrenceEnd =
    endKind === "count"
      ? { kind: "count", count: Number(form.get("count")) }
      : endKind === "until"
        ? { kind: "until", date: String(form.get("until") ?? "") }
        : { kind: "never" };

  if (repeat === "monthly") return { kind: "monthly", end };

  const weekdays = [
    ...new Set(
      form
        .getAll("weekday")
        .map(Number)
        .filter(
          (weekday) =>
            Number.isInteger(weekday) && weekday >= 1 && weekday <= 7,
        ),
    ),
  ];
  return { kind: "weekly", weekdays, end };
}

export function validateCalendarRegistrationForm(
  form: FormData,
): CalendarRegistrationErrors {
  const errors: CalendarRegistrationErrors = {};
  if (
    form.has("duration") &&
    (!Number.isInteger(Number(form.get("duration"))) ||
      Number(form.get("duration")) < 1)
  ) {
    errors.duration = "保持期間を板の設定範囲で入力してください";
  }
  const title = String(form.get("title") ?? "").trim();
  if (title.length === 0) {
    errors.title = "題名を入力してください";
  } else if ([...title].length > 60) {
    errors.title = "題名は60文字以内で入力してください";
  }

  if (String(form.get("startsAt") ?? "").length === 0) {
    errors.startsAt = "開始日時を入力してください";
  }

  const image = form.get("image");
  if (!(image instanceof File) || image.size === 0) {
    errors.image = "スレに使う画像を選択してください";
  }

  const threshold = Number(form.get("threshold"));
  if (
    String(form.get("threshold") ?? "").length === 0 ||
    !Number.isInteger(threshold) ||
    threshold < 0
  ) {
    errors.threshold = "人数は0以上の整数で入力してください";
  }

  const repeat = form.get("repeat");
  const end = form.get("end");
  const visibility = getCalendarFormVisibility(
    repeat === "weekly" || repeat === "monthly" ? repeat : "none",
    end === "count" || end === "until" ? end : "never",
  );
  if (visibility.showCount) {
    const count = Number(form.get("count"));
    if (
      String(form.get("count") ?? "").length === 0 ||
      !Number.isInteger(count) ||
      count < 1
    ) {
      errors.count = "回数は1以上の整数で入力してください";
    }
  }
  if (visibility.showUntil && String(form.get("until") ?? "").length === 0) {
    errors.until = "終了日を入力してください";
  }

  const pollOptions = String(form.get("pollOptions") ?? "")
    .split("\n")
    .map((option) => option.trim())
    .filter(Boolean);
  if (pollOptions.length === 1 || pollOptions.length > 5) {
    errors.pollOptions = "アンケートは2〜5択で入力してください";
  } else if (pollOptions.length >= 2) {
    const duration = Number(form.get("pollDuration"));
    if (!Number.isInteger(duration) || duration < 10 || duration > 1440) {
      errors.pollDuration = "受付期間は10〜1440分で入力してください";
    }
  }
  return errors;
}

export function currentCalendarMonth(): string {
  return nowInstant()
    .toZonedDateTimeISO("Asia/Tokyo")
    .toPlainDate()
    .toString()
    .slice(0, 7);
}
export function calendarRange(month: string): { from: string; to: string } {
  const date = Temporal.PlainYearMonth.from(month).toPlainDate({ day: 1 });
  return {
    from: date.toZonedDateTime("Asia/Tokyo").toInstant().toString(),
    to: date
      .add({ months: 1 })
      .toZonedDateTime("Asia/Tokyo")
      .toInstant()
      .toString(),
  };
}
export function calendarYearRange(year: number): { from: string; to: string } {
  const date = Temporal.PlainDate.from({ year, month: 1, day: 1 });
  return {
    from: date.toZonedDateTime("Asia/Tokyo").toInstant().toString(),
    to: date
      .add({ years: 1 })
      .toZonedDateTime("Asia/Tokyo")
      .toInstant()
      .toString(),
  };
}
export function calendarMonthGridWeeks(month: string): string[][] {
  const firstDay = Temporal.PlainYearMonth.from(month).toPlainDate({ day: 1 });
  const gridStart = firstDay.subtract({ days: firstDay.dayOfWeek - 1 });
  const weekCount = Math.ceil(
    (firstDay.dayOfWeek - 1 + firstDay.daysInMonth) / 7,
  );
  return Array.from({ length: weekCount }, (_, weekIndex) =>
    Array.from({ length: 7 }, (_, dayIndex) =>
      gridStart.add({ days: weekIndex * 7 + dayIndex }).toString(),
    ),
  );
}
export function eventDate(value: string): string {
  return Temporal.Instant.from(value)
    .toZonedDateTimeISO("Asia/Tokyo")
    .toPlainDate()
    .toString();
}
export function calendarWeekdayTone(
  date: string,
): "weekday" | "saturday" | "sunday" {
  const weekday = Temporal.PlainDate.from(date).dayOfWeek;
  if (weekday === 6) return "saturday";
  if (weekday === 7) return "sunday";
  return "weekday";
}
export function calendarWeekdayLabel(date: string): string {
  return ["月", "火", "水", "木", "金", "土", "日"][
    Temporal.PlainDate.from(date).dayOfWeek - 1
  ];
}
export function isCalendarToday(date: string, today: string): boolean {
  return date === today;
}
export function calendarDayNumber(date: string): string {
  return String(Temporal.PlainDate.from(date).day);
}
export function calendarListDateLabel(
  date: string,
  displayedMonth: string,
): string {
  if (date.startsWith(`${displayedMonth}-`)) return calendarDayNumber(date);
  const plainDate = Temporal.PlainDate.from(date);
  return `${plainDate.month}/${plainDate.day}`;
}
export function calendarDateLabel(date: string): string {
  const plainDate = Temporal.PlainDate.from(date);
  return `${plainDate.month}月${plainDate.day}日（${calendarWeekdayLabel(date)}）`;
}
export function eventTime(value: string): string {
  return Temporal.Instant.from(value)
    .toZonedDateTimeISO("Asia/Tokyo")
    .toPlainTime()
    .toString({ smallestUnit: "minute" });
}
export interface CalendarOccurrenceItem {
  startsAt: string;
}
export function splitCalendarOccurrences<T extends CalendarOccurrenceItem>(
  occurrences: readonly T[],
): { visible: T[]; overflow: T[] } {
  const sorted = [...occurrences].sort(
    (left, right) =>
      Temporal.Instant.from(left.startsAt).epochMilliseconds -
      Temporal.Instant.from(right.startsAt).epochMilliseconds,
  );
  return {
    visible: sorted.slice(0, 3),
    overflow: sorted.slice(3),
  };
}
export function selectCalendarOccurrence(
  occurrences: readonly EventOccurrence[],
  selectedAt: string | undefined,
  now: string,
): EventOccurrence | null {
  const sorted = [...occurrences].sort(
    (left, right) =>
      Temporal.Instant.from(left.startsAt).epochMilliseconds -
      Temporal.Instant.from(right.startsAt).epochMilliseconds,
  );
  if (selectedAt) {
    const selectedInstant = Temporal.Instant.from(selectedAt).epochMilliseconds;
    const selected = sorted.find(
      (occurrence) =>
        Temporal.Instant.from(occurrence.startsAt).epochMilliseconds ===
        selectedInstant,
    );
    if (selected) return selected;
  }
  const nowMilliseconds = Temporal.Instant.from(now).epochMilliseconds;
  return (
    sorted.find(
      (occurrence) =>
        Temporal.Instant.from(occurrence.startsAt).epochMilliseconds >=
        nowMilliseconds,
    ) ??
    sorted.at(-1) ??
    null
  );
}
export function eventDetailRange(
  selectedAt: string | undefined,
  calendarMonth: string,
): { from: string; to: string } {
  if (selectedAt) return calendarRange(eventDate(selectedAt).slice(0, 7));
  const start = Temporal.PlainYearMonth.from(calendarMonth).toPlainDate({
    day: 1,
  });
  return {
    from: start.toZonedDateTime("Asia/Tokyo").toInstant().toString(),
    to: start
      .add({ months: 12 })
      .toZonedDateTime("Asia/Tokyo")
      .toInstant()
      .toString(),
  };
}
export function resolveCalendarBoardFilter(
  isMixCatalog: boolean,
  boardKey: string,
  enabledMixSlugs: readonly string[],
): string | undefined {
  const slugs = isMixCatalog
    ? [...new Set(enabledMixSlugs)]
    : boardKey
      ? [boardKey]
      : [];
  return slugs.length > 0 ? slugs.join(",") : undefined;
}
export function recurrenceLabel(
  recurrence: CalendarEvent["recurrence"],
  firstStartsAt: string,
): string {
  if (recurrence.kind === "none") return "単発";

  const startDate = Temporal.Instant.from(firstStartsAt)
    .toZonedDateTimeISO("Asia/Tokyo")
    .toPlainDate();
  const label =
    recurrence.kind === "monthly"
      ? `毎月 ${startDate.day}日`
      : `毎週 ${[...new Set([startDate.dayOfWeek, ...recurrence.weekdays])]
          .filter((weekday) => weekday >= 1 && weekday <= 7)
          .sort((left, right) => left - right)
          .map(
            (weekday) =>
              ["月", "火", "水", "木", "金", "土", "日"][weekday - 1],
          )
          .join("・")}曜日`;
  const end = recurrence.end;
  const endLabel =
    end.kind === "count"
      ? `${end.count}回`
      : end.kind === "until"
        ? `${end.date}まで`
        : "無期限";
  return `${label} / ${endLabel}`;
}
export function calendarReasonLabel(reason: string): string {
  const reasons: Record<string, string> = {
    INSUFFICIENT_INTEREST: "興味ありの人数が不足しました",
    CANCELLED: "主催者による取り消し",
    CANCELLED_BY_MODERATOR: "運営による取り消し",
    ORGANIZER_BANNED: "主催者の利用制限による取り消し",
    BOARD_CLOSED: "板の閉鎖・書き込み停止",
    MODERATION_REJECTED: "検閲または板の設定により不成立",
    ATTACHMENT_UNAVAILABLE: "添付を利用できません",
    RETRY_EXHAUSTED: "スレ作成の再試行回数を超えました",
  };
  return reasons[reason] ?? reason;
}
export function classifyCalendarOccurrence(
  occurrence: Pick<EventOccurrence, "state" | "reason">,
): CalendarOccurrenceStatus {
  if (
    occurrence.reason === "CANCELLED" ||
    occurrence.reason === "CANCELLED_BY_MODERATOR" ||
    occurrence.reason === "ORGANIZER_BANNED"
  ) {
    return "cancelled";
  }
  const statusByState: Record<
    EventOccurrence["state"],
    CalendarOccurrenceStatus
  > = {
    SCHEDULED: "scheduled",
    PENDING: "pending",
    CREATED: "created",
    FAILED: "failed",
  };
  return statusByState[occurrence.state];
}
export function calendarOccurrenceTone(
  occurrence: Pick<EventOccurrence, "state" | "reason">,
): "scheduled" | "pending" | "created" | "inactive" {
  const status = classifyCalendarOccurrence(occurrence);
  if (status === "failed" || status === "cancelled") return "inactive";
  return status;
}
export function calendarOccurrenceStatusLabel(
  occurrence: Pick<EventOccurrence, "state" | "reason">,
): string {
  return {
    scheduled: "開催予定",
    pending: "判定中",
    created: "成立（自動スレ）",
    failed: "不成立",
    cancelled: "取り消し",
  }[classifyCalendarOccurrence(occurrence)];
}
export function calendarOccurrenceStatusDescription(
  occurrence: Pick<EventOccurrence, "state" | "reason">,
): string {
  const reason = occurrence.reason
    ? `、理由: ${calendarReasonLabel(occurrence.reason)}`
    : "";
  return `状態: ${calendarOccurrenceStatusLabel(occurrence)}${reason}`;
}
export function calendarOccurrenceStatusMark(
  occurrence: Pick<EventOccurrence, "state" | "reason">,
): string {
  return {
    scheduled: "",
    pending: "…",
    created: "✓",
    failed: "×",
    cancelled: "⊘",
  }[classifyCalendarOccurrence(occurrence)];
}
export interface CalendarInterestProgress {
  value: number;
  max: number;
  message: string;
}
export function calendarInterestProgress(
  occurrence: Pick<EventOccurrence, "state" | "reason" | "count">,
  threshold: number,
): CalendarInterestProgress | null {
  const status = classifyCalendarOccurrence(occurrence);
  if (status !== "scheduled" && status !== "pending") return null;

  const count = Math.max(0, occurrence.count);
  if (threshold <= 0) {
    return {
      value: 1,
      max: 1,
      message: "人数に関係なく立ちます",
    };
  }

  return {
    value: Math.min(count, threshold),
    max: threshold,
    message:
      count >= threshold
        ? "人数がそろいました"
        : `あと${threshold - count}人でスレが立ちます`,
  };
}
export function calendarInterestActionDescription(threshold: number): string {
  return threshold <= 0
    ? "開始時刻に自動でスレが立ちます"
    : `開始時刻に${threshold}人以上いれば自動でスレが立ちます`;
}
export function calendarOccurrenceAccessibleLabel(input: {
  occurrence: Pick<EventOccurrence, "startsAt" | "state" | "reason" | "count">;
  title: string;
  boardName: string;
  threshold: number;
}): string {
  return `${eventTime(input.occurrence.startsAt)} ${input.title}、${input.boardName}、興味あり${input.occurrence.count}/${input.threshold}、${calendarOccurrenceStatusDescription(input.occurrence)}`;
}
export function shouldShowEventInterestActions(input: {
  isMyEventsPage: boolean;
  isOrganizer: boolean;
  organizerLookupPending: boolean;
}): boolean {
  return (
    !input.isMyEventsPage && !input.isOrganizer && !input.organizerLookupPending
  );
}
export function getEventCancellationConfirmation(
  eventTitle: string,
  startsAt?: string,
): { title: string; message: string } {
  if (startsAt) {
    return {
      title: "この回を取り消しますか？",
      message: `「${eventTitle}」の${calendarDateLabel(eventDate(startsAt))} ${eventTime(startsAt)} JSTの回を取り消します。\n元に戻せません。`,
    };
  }
  return {
    title: "イベントを取り消しますか？",
    message: `「${eventTitle}」のイベント全体を取り消します。以降の回もすべて取り消されます。\n元に戻せません。`,
  };
}
export async function createEvent(
  form: FormData,
  deleteKey: string,
): Promise<{ id: string }> {
  const text = (key: string): string => String(form.get(key) ?? "");
  const recurrence = calendarRecurrenceFromForm(form);
  const file = form.get("image");
  if (!(file instanceof File) || file.size === 0)
    throw new Error("スレ立てには画像が必要です");
  const slug = text("slug");
  const attachmentId = await uploadAttachment(file, undefined, slug);
  const options = text("pollOptions")
    .split("\n")
    .map((label) => label.trim())
    .filter(Boolean);
  const r18 = form.has("r18");
  return apiPost<{ id: string }>(
    "/events",
    {
      slug,
      title: text("title").trim(),
      startsAt: Temporal.PlainDateTime.from(text("startsAt"))
        .toZonedDateTime("Asia/Tokyo")
        .toInstant()
        .toString(),
      recurrence,
      body: text("body"),
      attachmentIds: [attachmentId],
      threadOptions: {
        isSage: form.has("sage"),
        forceDisplayId: form.has("showId"),
        allowImageReplies: form.has("images"),
        threadNgWords: text("ng").split("\n").filter(Boolean),
      },
      moderation: {
        text: {
          sexual: r18 ? "off" : "on",
          exclusion: text("exclusion"),
          abuse: text("abuse"),
        },
        image: { nsfw: r18 ? "off" : "on" },
      },
      durationMinutes: Number(text("duration")),
      relay: form.has("relay"),
      threshold: Number(text("threshold")),
      ...(options.length
        ? { poll: { options, durationMinutes: Number(text("pollDuration")) } }
        : {}),
      deleteKey,
    },
    { requiresToken: true, credentials: "include", boardKey: slug },
  );
}
export async function setEventInterest(
  eventId: string,
  slug: string,
  startsAt: string | undefined,
  enabled: boolean,
  kind: "occurrence" | "series" | "exclude" = "occurrence",
): Promise<void> {
  await apiPutJson(
    `/events/${eventId}/interest`,
    { kind, occurrenceAt: startsAt, enabled },
    { requiresToken: true, credentials: "include", boardKey: slug },
  );
}
export async function cancelEvent(
  eventId: string,
  startsAt?: string,
): Promise<void> {
  await apiPost(
    `/events/${eventId}/cancel`,
    { occurrenceAt: startsAt },
    { requiresToken: true, credentials: "include" },
  );
}
export function fetchEventResults(): Promise<EventResults> {
  // The header polls results on every page, so it must never issue a token by itself.
  return apiGet("/events/results", {
    requiresToken: true,
    allowTokenIssue: false,
    credentials: "include",
  });
}
